import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import JsonLd from "@/components/ui/JsonLd";
import { SITE_NAME, absoluteUrl } from "@/lib/seo";

export interface Crumb {
  label: string;
  to?: string;
}

/**
 * Breadcrumb navigation with auto home link.
 * Usage: <Breadcrumbs crumbs={[{ label: "Seerah", to: "/seerah" }, { label: "Birth of the Prophet ﷺ" }]} />
 */
export default function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  const trail = [{ label: SITE_NAME, to: "/" }, ...crumbs];
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-[var(--color-ink-muted)] overflow-x-auto py-3">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: trail.map((crumb, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: crumb.label,
            ...(crumb.to ? { item: absoluteUrl(crumb.to) } : {}),
          })),
        }}
      />
      <Link
        to="/"
        className="flex items-center gap-1 hover:text-[var(--color-gold)] transition-colors shrink-0"
      >
        <Home className="w-3.5 h-3.5" />
        <span className="sr-only">Home</span>
      </Link>
      {crumbs.map((crumb, i) => (
        <span key={i} className="flex items-center gap-1.5 shrink-0">
          <ChevronRight className="w-3 h-3 text-[var(--color-cream-dark)]" />
          {crumb.to && i < crumbs.length - 1 ? (
            <Link to={crumb.to} className="hover:text-[var(--color-gold)] transition-colors">
              {crumb.label}
            </Link>
          ) : (
            <span className="text-[var(--color-midnight)] font-medium truncate max-w-[200px]">
              {crumb.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}
